import { useEffect, useState, useCallback } from 'react';
import { matchSorter } from 'match-sorter';
import { EMPTY_KEY } from 'components/StatusSummary/helper';
import { multiSelectValueHelper } from './filters.helper';

type DefaultData = { [key: string]: string | null | object | number | boolean };

type GetNestedKeys<T, K extends keyof T> = T[K] extends object
  ? `${Extract<K, string>}.${Extract<keyof T[K], string>}`
  : never;

type AllNestedKeys<T> = Exclude<
  {
    [K in keyof T]: GetNestedKeys<T, K>;
  }[keyof T],
  undefined
>;

export type FilterDefinitionsKeys<TData> =
  | keyof TData
  | AllNestedKeys<TData>
  | 'multipleFieldSearch'
  | 'columnFilter';

export type FilterDefinitions<TData> = Partial<{
  [key in FilterDefinitionsKeys<TData>]: string[];
}>;

const SPECIAL_FILTERS = {
  MULTIPLE_FIELD_SEARCH: 'multipleFieldSearch',
  COLUMN_FILTER: 'columnFilter',
} as const;

export const MULTIVALUE_MODE: { [key: string]: 'replacePrevious' | 'mergeWithPrevious' } = {
  REPLACE_PREVIOUS: 'replacePrevious',
  MERGE_WITH_PREVIOUS: 'mergeWithPrevious',
};

export type ChangeFilterProps<TData> = {
  filterName: keyof FilterDefinitions<TData>;
  filterValue?: string[];
  multivalueMode?: 'replacePrevious' | 'mergeWithPrevious';
};
function fuzzySearchMultipleWords(
  data: { [key: string]: string }[],
  keys: string[],
  filterValue: string,
) {
  if (!filterValue?.length) {
    return data;
  }
  return matchSorter(data, filterValue, {
    keys: [...keys, 'asset.name', 'children.*.id'],
    threshold: matchSorter.rankings.CONTAINS,
  });
}
const applyMultipleFieldSearch = (data: DefaultData[], fields: string[], text: string) => {
  const inputValue = String(text).replace(/[^a-zA-Z0-9-().,;:~`'"?!\p{L}]/gu, ' ');
  return fuzzySearchMultipleWords(data as { [key: string]: string }[], fields, inputValue);
};

export const matchesNestedProperty = <TData extends DefaultData>(
  item: TData,
  parentKey: string,
  childKey: string,
  currentFilterValue: string[],
  identical: boolean,
): boolean => {
  const parentValue = item[parentKey];
  if (!parentValue || typeof parentValue !== 'object') {
    return false;
  }
  if (!Array.isArray(parentValue) && parentValue && childKey in parentValue) {
    return matchesSimpleProperty(
      parentValue[childKey as keyof typeof parentValue],
      currentFilterValue,
      identical,
    );
  }

  return (
    Array.isArray(item[parentKey]) &&
    item[parentKey].some((parentListItem) => {
      if (
        !parentListItem ||
        typeof parentListItem !== 'object' ||
        !(childKey in (parentListItem as object))
      ) {
        return false;
      }

      return matchesSimpleProperty(parentListItem[childKey], currentFilterValue, identical);
    })
  );
};

const matchesSimpleProperty = (
  currentItemValue: unknown,
  currentFilterValue: string[],
  identical: boolean,
): boolean => {
  if (!currentItemValue || typeof currentItemValue === 'object') {
    return false;
  }

  return currentFilterValue.some((value) => {
    if (identical) {
      return currentItemValue === value;
    }
    return String(currentItemValue).toUpperCase().includes(value.toUpperCase());
  });
};

const shouldSkipFilter = (currentFilterValue: string[], currentItemValue: unknown): boolean => {
  const notShouldFilter =
    !currentFilterValue || currentFilterValue.length === 0 || currentFilterValue.includes('all');

  return notShouldFilter || (!currentItemValue && currentFilterValue.includes(EMPTY_KEY));
};

const applyFilterToItem = <TData extends DefaultData>(
  item: TData,
  filterName: keyof FilterDefinitions<TData>,
  currentFilterValue: string[],
  identical: boolean,
): boolean => {
  const currentItemValue = item[filterName];

  if (filterName === SPECIAL_FILTERS.MULTIPLE_FIELD_SEARCH) {
    return true;
  }

  if (filterName === SPECIAL_FILTERS.COLUMN_FILTER) {
    return currentFilterValue[0] in item;
  }

  if (shouldSkipFilter(currentFilterValue, currentItemValue)) {
    return true;
  }

  if (String(filterName).includes('.')) {
    const [parentKey, childKey] = String(filterName).split('.');
    return matchesNestedProperty(item, parentKey, childKey, currentFilterValue, identical);
  }

  return matchesSimpleProperty(currentItemValue, currentFilterValue, identical);
};

export const applyFiltersOnData = <TData extends DefaultData>(
  filtersToApply: FilterDefinitions<TData>,
  data: TData[],
  multipleFieldSearch?: Array<keyof FilterDefinitions<TData>>,
  identical = true,
) => {
  const filtersNames = Object.keys(filtersToApply) as Array<keyof FilterDefinitions<TData>>;

  const filteredData = filtersToApply['multipleFieldSearch']
    ? (applyMultipleFieldSearch(
        data,
        multipleFieldSearch as string[],
        String(filtersToApply['multipleFieldSearch']),
      ) as TData[])
    : data;

  return filteredData?.filter((item) =>
    filtersNames.every((filterName) => {
      const currentFilterValue = filtersToApply[filterName] as string[];
      return applyFilterToItem(item, filterName, currentFilterValue, identical);
    }),
  );
};

const useFilters = <TData extends DefaultData>({
  data,
  onChangeFilters,
  identical,
  multipleFieldSearch,
  defaultFilters = {},
}: {
  data: TData[];
  onChangeFilters: (data: TData[]) => void;
  identical?: boolean;
  multipleFieldSearch?: string[];
  defaultFilters?: Partial<{ [key in FilterDefinitionsKeys<TData>]: string[] }>;
}) => {
  const [allActiveFilters, setAllActiveFilters] =
    useState<FilterDefinitions<TData>>(defaultFilters);

  const applyFilters = useCallback(
    (filtersToApply: FilterDefinitions<TData>, data: TData[]): TData[] => {
      return applyFiltersOnData(filtersToApply, data, multipleFieldSearch, identical) ?? [];
    },
    [multipleFieldSearch, identical],
  );

  useEffect(() => {
    if (Object.keys(allActiveFilters).length === 0) {
      onChangeFilters(data);
      return;
    }
    const updatedData = data.length > 0 ? applyFilters(allActiveFilters, data) : [];

    onChangeFilters(updatedData);
  }, [data]);

  const onChange = ({
    filterName,
    filterValue,
    multivalueMode = MULTIVALUE_MODE.REPLACE_PREVIOUS,
  }: ChangeFilterProps<TData>) => {
    const newValues = multiSelectValueHelper<TData>({
      allActiveFilters,
      statusKey: filterName,
      newValue: filterValue ?? [],
    });
    const updatedFilters = {
      ...allActiveFilters,
      [filterName]:
        multivalueMode === MULTIVALUE_MODE.REPLACE_PREVIOUS || filterName === 'multipleFieldSearch'
          ? filterValue
          : newValues,
    };
    const updatedData = applyFilters(updatedFilters, data);
    setAllActiveFilters(updatedFilters);
    onChangeFilters(updatedData ?? []);
  };
  const resetFilters = () => {
    setAllActiveFilters(defaultFilters);
    onChangeFilters(data);
  };

  return {
    onChange,
    allActiveFilters,
    applyFilters,
    resetFilters,
  };
};

export default useFilters;
